<script module lang="ts">
	import type { Temporal } from '@js-temporal/polyfill'
	import { state_type, type StateResolve } from '#client/lib/client_type.ts'
	import type { ClientQueryFn } from '#client/lib/client_query_fn.ts'
	import AppScreen from '#client/component/AppScreen.svelte'
	import WindowCard from '#client/component/WindowCard.svelte'
	import Separator from '#client/component/Separator.svelte'
	import query_builder from '#shared/sql_request/typed_query_builder.ts'
	import type { Schema } from '#schema/types.ts'
	import type { ClientMetrics } from '#shared/type/client.ts'
	import type { FinancialNumber } from '#shared/fnum.ts'
	import { filter, map } from '#shared/array.ts'

	const fetch_projects_needing_estimate = (query: ClientQueryFn) => query(
		query_builder<Schema>()
			.from(`project`)
			.join(`client`, q => q.comparison(`client.client_id`, `=`, `project.client_id`))
			.join(`client_contact`, q => q.comparison(`client_contact.client_contact_id`, `=`, `project.client_contact_id`))
			.join(`project_document`, q => q.comparison(`project_document.project_document_id`, `=`, `project.project_document_id`))
			.where(q => q.comparison(`project_document.needs_estimate_to_move_on`, `=`, { value: true }))
			.where(q => q.comparison(`project.closed`, `=`, { value: false }))
			.select(() => [
				`project.project_id`,
				`project.address_line_1`,
				`project.address_line_2`,
				`project.city`,
				`project.lead_details`,
				`project.created_at`,
				`client.client_id`,
				`client.name`,
				`client_contact.name`,
			] as const)
			.order_by(`project.created_at`, `ASC`)
			.build()
	)

	export const asr_state = state_type({
		name: `app.menu.leads_to_estimate`,
		route: `/leads_to_estimate`,
		resolve: async ({ query, session, server }) => {
			const [projects, logged_in_session] = await Promise.all([
				fetch_projects_needing_estimate(query),
				session.get_logged_in(),
			])
			return { projects, timezone: logged_in_session.company.timezone, server }
		},
	})

	type ProjectRow = StateResolve<typeof asr_state>[`projects`][number]

	const format_thousands = (amount: FinancialNumber) => `${amount.times(`0.001`).changeDecimalPlaces(0).toString()}k`
	const loading_status_field = '...'
</script>

<script lang="ts">
	const { projects, timezone, server, asr }: StateResolve<typeof asr_state> & { asr: StateAsr } = $props()

	let metrics_by_client_id = $state.raw<ReadonlyMap<bigint, ClientMetrics> | null>(null)

	// svelte-ignore state_referenced_locally
	const client_ids = [...new Set(map(projects, row => row.client.client_id))]
	// svelte-ignore state_referenced_locally
	void server.get_client_metrics({ client_ids }).then(metrics => {
		metrics_by_client_id = new Map(map(metrics, client_metrics => [client_metrics.client_id, client_metrics]))
	})

	const format_date = (instant: Temporal.Instant) => instant.toZonedDateTimeISO(timezone).toPlainDate().toString()
</script>

{#snippet field(long: string, short: string)}
	<p class="status-bar-field" title={long}>
		<span class="long">{long}</span>
		<span class="short">{short}</span>
	</p>
{/snippet}

<AppScreen>
	<h1>Leads To Estimate</h1>

	<div class="cards">
		{#each projects as row (row.project.project_id)}
			<WindowCard title={row.client.name} href={asr.makePath(`app.estimate`, { project_id: row.project.project_id })}>
				{#snippet status_bar()}
					{@const created = format_date(row.project.created_at)}
					{@const metrics = metrics_by_client_id?.get(row.client.client_id)}
					{@render field(`Created ${created}`, created)}
					{#if metrics}
						{@const accepted = `${metrics.accepted}/${metrics.proposals}`}
						{@render field(`Accepted: ${accepted}`, accepted)}
						{@const last_year = metrics.latest_job_count === 0n
							? `0`
							: `$${format_thousands(metrics.latest_jobs_total)} (${metrics.latest_job_count})`}
						{@render field(`Last year: ${last_year}`, last_year)}
					{:else}
						{@render field(loading_status_field, loading_status_field)}
						{@render field(loading_status_field, loading_status_field)}
					{/if}
				{/snippet}
				<div class="body">
					<div class="half location">
						<div>{row.client_contact.name}</div>
						<div>{filter([row.project.address_line_1, row.project.address_line_2, row.project.city], Boolean).join(`, `)}</div>
					</div>
					{#if row.project.lead_details !== ``}
						<Separator />
						<div class="half lead-details">{row.project.lead_details}</div>
					{/if}
				</div>
			</WindowCard>
		{/each}
	</div>
</AppScreen>

<style>
	.cards {
		display: flex;
		flex-direction: column;
		gap: var(--gap_unit);
		container-type: inline-size;
	}

	.body {
		display: flex;
		flex-direction: column;
		gap: var(--gap_half);
	}

	@container (min-width: 40rem) {
		.body {
			flex-direction: row;
		}
	}

	.body > .half {
		flex: 1 1 0;
	}

	.location {
		display: flex;
		flex-direction: column;
	}

	.lead-details {
		white-space: pre-wrap;
	}

	.short {
		display: none;
	}

	@media (max-width: 400px) {
		.long {
			display: none;
		}

		.short {
			display: inline;
		}
	}
</style>
